import { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import MaskedCost from './MaskedCost';

// Collaboration History — a creator's past brand collaborations, shown as a modal
// from the profile Contact area. Data: `past_collaborations[]` on the profile
// response. Cost + the collaboration contact (direct = creator, vendor = vendor POC)
// are gated to internal staff via `is_staff` (localStorage is_superuser fallback).
// Premium "deal record" presentation: navy + gold, status accent rail, hero cost.

function readSuperuser() {
  try {
    return !!JSON.parse(localStorage.getItem('user') || '{}').is_superuser;
  } catch {
    return false;
  }
}

const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return (v / 1e7).toFixed(v % 1e7 ? 1 : 0) + 'Cr';
  if (v >= 1e5) return (v / 1e5).toFixed(v % 1e5 ? 1 : 0) + 'L';
  if (v >= 1e3) return (v / 1e3).toFixed(v % 1e3 ? 1 : 0) + 'K';
  return String(v);
};

const moneySym = (cur) => (cur === 'INR' || !cur ? '₹' : cur + ' ');
const fmtMoney = (n, cur) => moneySym(cur) + (Number(n) || 0).toLocaleString('en-IN');

// Format a campaign date. Accepts a full date/ISO string ("2026-06-24" / ISO) →
// "24 Jun 2026", or a month-only string ("2026-06") → "Jun 2026". Returns the raw
// string if it can't be parsed, and '' for empty.
const fmtDate = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  const monthOnly = s.match(/^(\d{4})-(\d{2})$/);
  if (monthOnly) {
    const dt = new Date(Number(monthOnly[1]), Number(monthOnly[2]) - 1, 1);
    return Number.isNaN(dt.getTime()) ? s : dt.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime())
    ? s
    : dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// The campaign date field the backend will add. We read a few likely names so the
// UI works regardless of the exact key chosen.
const collabDateOf = (c) => c.campaign_date || c.collab_date || c.campaign_month || c.date || '';

const PLATFORM = {
  instagram: { label: 'Instagram', color: '#E1306C' },
  youtube: { label: 'YouTube', color: '#FF0000' },
};

const initials = (name) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';

// Phone icon (SVG, not emoji)
const PhoneIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.1 21 3 13.9 3 5c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1l-2.2 2.2z" />
  </svg>
);

// Dedupe collaborations by project_code (the per-campaign key), keeping the most
// recently imported copy so re-imports never stack duplicate rows. Entries with
// no project_code are always kept. Result is sorted newest-first by imported_at.
function dedupeCollaborations(list) {
  const arr = Array.isArray(list) ? list : [];
  const byCode = new Map();
  const noCode = [];
  for (const c of arr) {
    const code = c && c.project_code;
    if (!code) { noCode.push(c); continue; }
    const prev = byCode.get(code);
    if (!prev || String(c.imported_at || '') > String(prev.imported_at || '')) byCode.set(code, c);
  }
  return [...byCode.values(), ...noCode].sort(
    (a, b) => String(b.imported_at || '').localeCompare(String(a.imported_at || ''))
  );
}

function CollaborationHistory({ past_collaborations, is_staff, onClose }) {
  const classes = useStyles();
  const items = dedupeCollaborations(past_collaborations);
  const showCosts = is_staff === undefined ? readSuperuser() : !!is_staff;

  // Close on Escape (robust — fires regardless of where focus sits).
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={classes.panel}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headLeft}>
          <span className={classes.kicker}>PAST WORK</span>
          <h2 className={classes.title}>Collaboration History</h2>
        </div>
        <div className={classes.headRight}>
          <span className={classes.count}>
            {items.length} {items.length === 1 ? 'collab' : 'collabs'}
          </span>
          {onClose && (
            <button className={classes.close} onClick={onClose} aria-label="Close">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </div>
        <div className={classes.headGlow} />
      </div>

      <div className={classes.body}>
        {items.length === 0 ? (
          <div className={classes.empty}>No past collaborations on record.</div>
        ) : (
          items.map((c, i) => {
            const isAgency = !!(c.managed_by && c.managed_by.toLowerCase() !== 'direct');
            const agencyName = c.vendor_name || (isAgency ? c.managed_by : '');
            const accent = isAgency ? '#14B8A6' : '#0095FF';
            const plat = PLATFORM[(c.platform || '').toLowerCase()];

            return (
              <div className={classes.card} key={c.project_code || i}>
                {/* Top: monogram + brand + platform */}
                <div className={classes.cardTop}>
                  <div className={classes.mono}>{initials(c.brand)}</div>
                  <div className={classes.brandWrap}>
                    <span className={classes.brand}>{c.brand || 'Unknown brand'}</span>
                    <span className={classes.brandSub}>{c.category || 'Collaboration'}</span>
                  </div>
                  <div className={classes.topRight}>
                    {plat && (
                      <span className={classes.platform} style={{ color: plat.color, borderColor: plat.color }}>
                        {plat.label}
                      </span>
                    )}
                    <span
                      className={classes.managed}
                      style={{ color: accent, background: `${accent}1A`, borderColor: `${accent}40` }}
                    >
                      {isAgency ? 'Agency' : 'Direct'}
                    </span>
                  </div>
                </div>

                {/* Deliverables — the headline of the collaboration */}
                {c.deliverables && (
                  <div className={classes.deliver}>
                    <span className={classes.deliverLabel}>Deliverables</span>
                    <p className={classes.deliverText}>{c.deliverables}</p>
                  </div>
                )}

                {/* Stat strip */}
                <div className={classes.stats}>
                  {agencyName && isAgency && (
                    <div className={classes.statCell}>
                      <span className={classes.statKey}>Vendor</span>
                      <span className={classes.statVal}>{agencyName}</span>
                    </div>
                  )}
                  {collabDateOf(c) && (
                    <div className={classes.statCell}>
                      <span className={classes.statKey}>Campaign date</span>
                      <span className={classes.statVal}>{fmtDate(collabDateOf(c))}</span>
                    </div>
                  )}
                  {c.team_lead && (
                    <div className={classes.statCell}>
                      <span className={classes.statKey}>Internal lead</span>
                      <span className={classes.statVal}>{c.team_lead}</span>
                    </div>
                  )}
                  {c.internal_poc_name && (
                    <div className={classes.statCell}>
                      <span className={classes.statKey}>Internal POC</span>
                      <span className={classes.statVal}>{c.internal_poc_name}</span>
                    </div>
                  )}
                  {Number(c.followers_at_time) > 0 && (
                    <div className={classes.statCell}>
                      <span className={classes.statKey}>Followers then</span>
                      <span className={classes.statVal}>{fmtCompact(c.followers_at_time)}</span>
                    </div>
                  )}
                </div>

                {/* Contact (staff) — masked by default, click to reveal, auto re-mask */}
                {showCosts && c.contact_details && (
                  <div className={classes.contact}>
                    <span className={classes.contactIcon}><PhoneIcon /></span>
                    <span className={classes.contactLabel}>{isAgency ? 'Vendor POC' : 'Direct contact'}</span>
                    <span className={classes.contactValWrap}>
                      <MaskedCost value={String(c.contact_details)} valueClassName={classes.contactVal} dark />
                    </span>
                  </div>
                )}

                {/* Footer: ref + report (left), hero cost (right) */}
                <div className={classes.foot}>
                  <div className={classes.footMeta}>
                    {c.project_code && <span className={classes.code}>{c.project_code}</span>}
                    {c.dashboard_link ? (
                      <a href={c.dashboard_link} target="_blank" rel="noopener noreferrer" className={classes.report}>
                        Campaign Report ↗
                      </a>
                    ) : (
                      <span className={classes.reportNone}>No report linked</span>
                    )}
                  </div>
                  {showCosts && Number(c.internal_cost) > 0 && (
                    <div className={classes.cost}>
                      <span className={classes.costLabel}>Cost</span>
                      <MaskedCost value={fmtMoney(c.internal_cost, c.currency)} valueClassName={classes.costVal} />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const FONT = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif';
const NAVY = '#111936';
const GOLD = '#FEBD1C';
const MUTED = '#8A90A6';

const useStyles = makeStyles(() => ({
  panel: {
    fontFamily: FONT,
    background: '#FFFFFF',
    borderRadius: 18,
    overflow: 'hidden',
    boxShadow: '0 28px 64px -16px rgba(17,25,54,0.45), 0 8px 24px rgba(17,25,54,0.12)',
  },
  header: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 22px 18px',
    background: `linear-gradient(120deg, ${NAVY} 0%, #1B2452 60%, #232C5E 100%)`,
    borderBottom: `3px solid ${GOLD}`,
    overflow: 'hidden',
  },
  headLeft: { position: 'relative', zIndex: 1 },
  headGlow: {
    position: 'absolute', top: -70, right: 40, width: 200, height: 200,
    background: `radial-gradient(circle, ${GOLD}26 0%, transparent 70%)`, pointerEvents: 'none',
  },
  kicker: { fontSize: 10, fontWeight: 800, letterSpacing: '0.2em', color: GOLD },
  title: { margin: '5px 0 0', fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', color: '#fff' },
  headRight: { position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 },
  count: {
    fontSize: 11.5, fontWeight: 700, color: GOLD, letterSpacing: '0.02em',
    background: 'rgba(254,189,28,0.14)', border: '1px solid rgba(254,189,28,0.30)',
    borderRadius: 999, padding: '4px 11px',
  },
  close: {
    width: 30, height: 30, borderRadius: 999, border: 'none', flexShrink: 0,
    background: 'rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 150ms ease', '&:hover': { background: GOLD, color: NAVY },
  },

  body: { padding: '16px 18px 18px', background: '#F6F7FC', maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' },
  empty: { padding: '40px 0', textAlign: 'center', fontSize: 13.5, color: MUTED },

  card: {
    position: 'relative',
    background: '#FFFFFF',
    border: '1px solid #E7E9F5',
    borderRadius: 14,
    padding: '16px 16px 14px',
    marginBottom: 12,
    overflow: 'hidden',
    transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
    '&:last-child': { marginBottom: 0 },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 16px 32px -16px rgba(17,25,54,0.30)',
      borderColor: '#D7DAEE',
    },
  },
  cardTop: { display: 'flex', alignItems: 'center', gap: 12 },
  mono: {
    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: NAVY, color: GOLD, fontWeight: 800, fontSize: 15, letterSpacing: '0.02em',
  },
  brandWrap: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
  brand: { fontSize: 16.5, fontWeight: 800, color: NAVY, letterSpacing: '-0.01em', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  brandSub: { fontSize: 11.5, fontWeight: 500, color: MUTED, marginTop: 2 },
  topRight: { display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 },
  platform: { fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', border: '1.5px solid', borderRadius: 6, padding: '3px 7px' },
  managed: { fontSize: 9.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', border: '1px solid', borderRadius: 6, padding: '3px 8px' },

  deliver: { marginTop: 14, padding: '10px 12px', background: '#FBFAF3', border: '1px solid #F3ECD0', borderLeft: `3px solid ${GOLD}`, borderRadius: 9 },
  deliverLabel: { display: 'block', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.1em', color: '#B08A1E', textTransform: 'uppercase', marginBottom: 3 },
  deliverText: { margin: 0, fontSize: 13, fontWeight: 600, color: NAVY, lineHeight: 1.4 },

  stats: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '10px 16px', marginTop: 14 },
  statCell: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  statKey: { fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', color: MUTED, textTransform: 'uppercase' },
  statVal: { fontSize: 13, fontWeight: 700, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },

  contact: {
    display: 'flex', alignItems: 'center', gap: 9, marginTop: 14, padding: '10px 13px',
    background: NAVY, borderRadius: 10, textDecoration: 'none', cursor: 'pointer',
    transition: 'opacity 150ms ease', '&:hover': { opacity: 0.92 },
  },
  contactIcon: { color: GOLD, display: 'flex', flexShrink: 0 },
  contactLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase' },
  contactValWrap: { marginLeft: 'auto', display: 'flex', alignItems: 'center' },
  contactVal: { fontSize: 14, fontWeight: 800, color: '#fff', letterSpacing: '0.03em', fontFamily: 'ui-monospace,Menlo,monospace' },

  foot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, paddingTop: 13, borderTop: '1px solid #EEF0FA', flexWrap: 'wrap' },
  footMeta: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 },
  code: { fontSize: 11, fontWeight: 700, color: MUTED, fontFamily: 'ui-monospace,Menlo,monospace', letterSpacing: '0.02em' },
  report: { fontSize: 11.5, fontWeight: 700, color: '#0066B3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
  reportNone: { fontSize: 11, fontWeight: 500, color: '#C2C7E2', fontStyle: 'italic' },
  cost: { display: 'flex', alignItems: 'baseline', gap: 8, flexShrink: 0 },
  costLabel: { fontSize: 9.5, fontWeight: 800, letterSpacing: '0.08em', color: '#B08A1E', textTransform: 'uppercase' },
  costVal: { fontSize: 19, fontWeight: 800, color: NAVY, letterSpacing: '-0.01em' },
}));

export default CollaborationHistory;
